import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Navigate } from "react-router-dom";
import { isCrmAdmin } from "../common/crmRoles";
import {
    Box, Typography, Card, Button, CircularProgress, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    IconButton, Tooltip, Table, TableHead, TableBody, TableRow, TableCell,
    TableContainer,
} from "@mui/material";
import {
    AddCircleOutline, EditOutlined, DeleteOutline, Close, LayersOutlined, Refresh,
    KeyboardArrowUp, KeyboardArrowDown,
} from "@mui/icons-material";
import { WB_SX } from "../../../ui/ux/uiTokens";
import axiosInstance from "../../../api/axiosInstance";
import base_url from "../../../constants";
import useNotification from "../../common/Notification";
import {
    getSid, sortStagesByOrdering, getDefaultStageOrdering, parseStageOrdering,
} from "./pipelineStageUtils";

const STAGE_COLOR_PALETTE = [
    { bg: "#e0e7ff", text: "#3730a3" },
    { bg: "#dbeafe", text: "#1d4ed8" },
    { bg: "#fef9c3", text: "#854d0e" },
    { bg: "#ffe4e6", text: "#be123c" },
    { bg: "#d1fae5", text: "#065f46" },
    { bg: "#fce7f3", text: "#9d174d" },
];

const normaliseArray = (raw, ...keys) => {
    if (Array.isArray(raw)) return raw;
    for (const k of keys) if (Array.isArray(raw?.[k])) return raw[k];
    return [];
};

// ─── Create stage ─────────────────────────────────────────────────────────────
const CreateStageDialog = ({ open, onClose, onCreated, defaultOrdering }) => {
    const notify = useNotification();
    const [name, setName] = useState("");
    const [ordering, setOrdering] = useState("0");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setName("");
            setOrdering(String(defaultOrdering ?? 0));
        }
    }, [open, defaultOrdering]);

    const handleSubmit = async () => {
        if (!name.trim()) return;
        const orderNum = parseStageOrdering(ordering);
        if (ordering.trim() !== "" && orderNum === null) {
            notify("Display order must be 0 or greater", "error");
            return;
        }
        setSaving(true);
        try {
            const body = { stage_name: name.trim() };
            if (orderNum !== null) body.ordering = orderNum;
            const res = await axiosInstance.post(`${base_url}/crm/pipeline/stages`, body);
            notify("Stage created successfully", "success");
            onCreated(res.data?.stage || res.data?.data || res.data);
            setName("");
            onClose();
        } catch (err) {
            notify(err?.response?.data?.message || "Failed to create stage", "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem", pb: 1 }}>Create pipeline stage</DialogTitle>
            <DialogContent sx={{ pt: "8px !important", display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                    autoFocus
                    fullWidth
                    label="Stage name"
                    placeholder="e.g. Proposal, Negotiation…"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                    size="small"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
                <TextField
                    fullWidth
                    label="Display order"
                    type="number"
                    inputProps={{ min: 0, step: 1 }}
                    value={ordering}
                    onChange={(e) => setOrdering(e.target.value)}
                    size="small"
                    helperText="Lower numbers appear first in the pipeline"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                <Button onClick={onClose} size="small" sx={WB_SX.secondaryButton}>Cancel</Button>
                <Button variant="contained" size="small" disabled={!name.trim() || saving} onClick={handleSubmit} sx={WB_SX.primaryButton}>
                    {saving ? "Creating…" : "Create"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ─── Edit stage (name only — use arrows + Save order for position) ───────────
const EditStageDialog = ({ open, stage, onClose, onUpdated }) => {
    const notify = useNotification();
    const [name, setName] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (stage) setName(stage.stage_name || "");
    }, [stage]);

    const handleSubmit = async () => {
        const sid = getSid(stage);
        if (!name.trim() || sid == null) return;
        if (name.trim() === (stage?.stage_name || "")) {
            onClose();
            return;
        }
        setSaving(true);
        try {
            await axiosInstance.put(`${base_url}/crm/pipeline/stages/${sid}`, { stage_name: name.trim() });
            notify("Stage updated", "success");
            onUpdated();
            onClose();
        } catch (err) {
            notify(err?.response?.data?.message || "Failed to update stage", "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem", pb: 1 }}>Edit stage</DialogTitle>
            <DialogContent sx={{ pt: "8px !important" }}>
                <TextField
                    autoFocus
                    fullWidth
                    label="Stage name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    size="small"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                <Button onClick={onClose} size="small" sx={WB_SX.secondaryButton}>Cancel</Button>
                <Button variant="contained" size="small" disabled={!name.trim() || saving} onClick={handleSubmit} sx={WB_SX.primaryButton}>
                    {saving ? "Saving…" : "Save"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ─── Page — Manage stages nav is restricted to CRM-ADMIN (see navConfig) ───────
const CRMPipelineStages = () => {
    const userIsCrmAdmin = useMemo(() => isCrmAdmin(), []);
    const _notify = useNotification();
    const notifyRef = useRef(_notify);
    notifyRef.current = _notify;

    const [stages, setStages] = useState([]);
    const [draftStages, setDraftStages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [editStage, setEditStage] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [savingOrder, setSavingOrder] = useState(false);

    const sortedStages = useMemo(() => sortStagesByOrdering(stages), [stages]);
    const defaultOrdering = useMemo(() => getDefaultStageOrdering(stages), [stages]);

    const hasOrderChanges = useMemo(() => {
        if (draftStages.length !== sortedStages.length) return false;
        return draftStages.some((s, i) => getSid(s) !== getSid(sortedStages[i]));
    }, [draftStages, sortedStages]);

    const syncDraftFromSaved = useCallback((list) => {
        setDraftStages(sortStagesByOrdering(list));
    }, []);

    const fetchStages = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get(`${base_url}/crm/pipeline/stages`);
            const raw = res.data;
            const list = Array.isArray(raw?.data)
                ? raw.data
                : Array.isArray(raw?.data?.data)
                    ? raw.data.data
                    : normaliseArray(raw, "stages");
            setStages(list);
            syncDraftFromSaved(list);
        } catch (err) {
            const msg = err?.response?.data?.message || "Failed to load pipeline stages";
            setError(msg);
            notifyRef.current(msg, "error");
        } finally {
            setLoading(false);
        }
    }, [syncDraftFromSaved]);

    useEffect(() => {
        if (!userIsCrmAdmin) {
            setLoading(false);
            return;
        }
        fetchStages();
    }, [fetchStages, userIsCrmAdmin]);

    const handleCreated = () => {
        fetchStages();
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await axiosInstance.delete(`${base_url}/crm/pipeline/stages/${getSid(deleteTarget)}`);
            notifyRef.current("Stage deleted", "success");
            const remaining = stages.filter((s) => getSid(s) !== getSid(deleteTarget));
            setStages(remaining);
            setDraftStages((prev) => prev.filter((s) => getSid(s) !== getSid(deleteTarget)));
            setDeleteTarget(null);
        } catch (err) {
            notifyRef.current(err?.response?.data?.message || "Cannot delete stage — it may have active or open deals", "error");
        } finally {
            setDeleting(false);
        }
    };

    const moveStage = (index, direction) => {
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= draftStages.length) return;
        setDraftStages((prev) => {
            const next = [...prev];
            [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
            return next;
        });
    };

    const discardOrderChanges = () => {
        syncDraftFromSaved(stages);
    };

    const saveOrder = async () => {
        if (!hasOrderChanges || !draftStages.length) return;
        setSavingOrder(true);
        try {
            await Promise.all(
                draftStages.map((stage, index) =>
                    axiosInstance.put(`${base_url}/crm/pipeline/stages/${getSid(stage)}`, { ordering: index })
                )
            );
            notifyRef.current("Stage order saved", "success");
            await fetchStages();
        } catch (err) {
            notifyRef.current(err?.response?.data?.message || "Failed to save stage order", "error");
        } finally {
            setSavingOrder(false);
        }
    };

    const handleRefresh = () => {
        if (hasOrderChanges && !window.confirm("Discard unsaved order changes and refresh?")) return;
        fetchStages();
    };

    if (!userIsCrmAdmin) {
        return <Navigate to="/crm/dashboard" replace />;
    }

    return (
        <Box sx={WB_SX.page}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3, flexWrap: "wrap", gap: 2 }}>
                <Box>
                    <Typography variant="h5" sx={WB_SX.pageTitle}>Manage pipeline stages</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Use the arrows to reorder stages (0 = top). Click <strong>Save order</strong> to apply changes to the pipeline.
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                    {hasOrderChanges && (
                        <>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={discardOrderChanges}
                                disabled={savingOrder}
                                sx={WB_SX.secondaryButton}
                            >
                                Discard
                            </Button>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={saveOrder}
                                disabled={savingOrder}
                                sx={WB_SX.primaryButton}
                            >
                                {savingOrder ? "Saving…" : "Save order"}
                            </Button>
                        </>
                    )}
                    <Tooltip title="Refresh list">
                        <IconButton size="small" onClick={handleRefresh} disabled={loading || savingOrder} sx={WB_SX.secondaryButton}>
                            <Refresh fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Button variant="contained" size="small" startIcon={<AddCircleOutline fontSize="small" />} onClick={() => setCreateOpen(true)} sx={WB_SX.primaryButton}>
                        Add stage
                    </Button>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            {hasOrderChanges && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    You have unsaved order changes. Position <strong>0</strong> is the top of the pipeline; higher numbers appear further down.
                </Alert>
            )}

            <Card elevation={0} sx={WB_SX.card}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={WB_SX.tableHeadCell} width={88}>Order</TableCell>
                                <TableCell sx={WB_SX.tableHeadCell}>Stage name</TableCell>
                                <TableCell sx={WB_SX.tableHeadCell} align="right" width={160}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                                        <CircularProgress size={28} />
                                    </TableCell>
                                </TableRow>
                            ) : draftStages.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
                                            <LayersOutlined sx={{ fontSize: 40, color: "text.disabled" }} />
                                            <Typography variant="body2" color="text.secondary">No stages yet. Create your first stage to start tracking deals.</Typography>
                                            <Button size="small" variant="contained" onClick={() => setCreateOpen(true)} sx={WB_SX.primaryButton}>
                                                Add stage
                                            </Button>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                draftStages.map((s, i) => {
                                    const sid = getSid(s);
                                    const c = STAGE_COLOR_PALETTE[i % STAGE_COLOR_PALETTE.length];
                                    const savedIndex = sortedStages.findIndex((x) => getSid(x) === sid);
                                    const moved = savedIndex !== -1 && savedIndex !== i;
                                    return (
                                        <TableRow
                                            key={sid}
                                            sx={{
                                                ...WB_SX.tableRow,
                                                background: moved ? "rgba(37,99,235,0.04)" : i % 2 === 0 ? "#fff" : "#fafafa",
                                            }}
                                        >
                                            <TableCell>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: 700,
                                                            minWidth: 20,
                                                            color: moved ? "#2563eb" : "text.secondary",
                                                        }}
                                                    >
                                                        {i}
                                                    </Typography>
                                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                                        <Tooltip title="Move up">
                                                            <span>
                                                                <IconButton
                                                                    size="small"
                                                                    disabled={i === 0 || savingOrder}
                                                                    onClick={() => moveStage(i, -1)}
                                                                    sx={{ p: 0.25, color: "text.secondary" }}
                                                                >
                                                                    <KeyboardArrowUp sx={{ fontSize: 18 }} />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                        <Tooltip title="Move down">
                                                            <span>
                                                                <IconButton
                                                                    size="small"
                                                                    disabled={i === draftStages.length - 1 || savingOrder}
                                                                    onClick={() => moveStage(i, 1)}
                                                                    sx={{ p: 0.25, color: "text.secondary" }}
                                                                >
                                                                    <KeyboardArrowDown sx={{ fontSize: 18 }} />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: c.text }} />
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.stage_name}</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Tooltip title="Edit name">
                                                    <IconButton size="small" onClick={() => setEditStage(s)} sx={{ color: "text.secondary", "&:hover": { color: "#2563eb" } }}>
                                                        <EditOutlined fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton size="small" onClick={() => setDeleteTarget(s)} sx={{ color: "text.secondary", "&:hover": { color: "#dc2626" } }}>
                                                        <DeleteOutline fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>

            <CreateStageDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={handleCreated}
                defaultOrdering={defaultOrdering}
            />
            <EditStageDialog
                open={!!editStage}
                stage={editStage}
                onClose={() => setEditStage(null)}
                onUpdated={fetchStages}
            />
            <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>Delete stage</Typography>
                    <IconButton size="small" onClick={() => !deleting && setDeleteTarget(null)} disabled={deleting}>
                        <Close fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        Are you sure you want to delete <strong>&quot;{deleteTarget?.stage_name}&quot;</strong>?
                        Deletion is blocked if this stage has active or open deals.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button size="small" onClick={() => setDeleteTarget(null)} sx={WB_SX.secondaryButton} disabled={deleting}>Cancel</Button>
                    <Button
                        size="small"
                        variant="contained"
                        onClick={confirmDelete}
                        disabled={deleting}
                        sx={{ ...WB_SX.primaryButton, backgroundColor: "#dc2626", "&:hover": { backgroundColor: "#b91c1c" } }}
                    >
                        {deleting ? "Deleting…" : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CRMPipelineStages;
